import type { Seat, VoiceSignal } from './protocol';

/** What the UI needs to reflect: the local mic + the peer-connection lifecycle. */
export type VoiceStatus = 'off' | 'waiting' | 'connecting' | 'live' | 'failed';

export interface VoiceCallbacks {
  /** Send one signaling beat to the peer (wrapped as a `voice` message upstream). */
  send(signal: VoiceSignal): void;
  /** Our current seat — decides the polite/impolite role for glare resolution. */
  seat(): Seat;
  /** Surface state changes (status + whether we're transmitting) to the UI. */
  onStatus(status: VoiceStatus, muted: boolean): void;
}

// Public STUN only: enough for most home/office NATs. Behind symmetric NAT a TURN
// relay would be needed — set one via VITE_TURN_URL / _USER / _CRED to add it.
function iceServers(): RTCIceServer[] {
  const servers: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
  const env = import.meta.env as Record<string, string | undefined>;
  const turn = env.VITE_TURN_URL;
  if (turn) servers.push({ urls: turn, username: env.VITE_TURN_USER, credential: env.VITE_TURN_CRED });
  return servers;
}

/**
 * Peer-to-peer voice chat between the two players in a room. The room WebSocket
 * carries only the WebRTC handshake (offer / answer / ICE); the audio streams
 * directly between the two browsers.
 *
 * Voice is opt-in: each player toggles their own microphone. A small ready/bye
 * handshake means we only open a connection once BOTH players have joined voice,
 * and the connection is (re)built using the canonical "perfect negotiation"
 * pattern so simultaneous offers never deadlock.
 */
export class VoiceChat {
  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private readonly audioEl: HTMLAudioElement;

  private localReady = false; // our mic is on and we want to talk
  private peerReady = false; // the peer has their mic on too
  private muted = false;

  // perfect-negotiation bookkeeping
  private makingOffer = false;
  private ignoreOffer = false;

  constructor(private readonly cb: VoiceCallbacks) {
    // a detached <audio> element plays the remote track (autoplay, not muted)
    this.audioEl = document.createElement('audio');
    this.audioEl.autoplay = true;
    this.audioEl.style.display = 'none';
    document.body.appendChild(this.audioEl);
  }

  get active(): boolean {
    return this.localReady;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Mic button: turn voice on if off, off if on. */
  async toggle(): Promise<void> {
    if (this.localReady) this.leave();
    else await this.join();
  }

  /** Acquire the microphone and announce readiness to the peer. */
  private async join(): Promise<void> {
    if (this.localReady) return;
    this.emit('connecting');
    try {
      this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch {
      this.emit('failed');
      return;
    }
    this.muted = false;
    this.localReady = true;
    this.cb.send({ ready: true });
    if (this.peerReady) this.connect();
    else this.emit('waiting'); // mic is hot; just waiting for the peer to join voice
  }

  /** Drop out of voice: stop the mic, tear down the connection, tell the peer. */
  leave(): void {
    if (!this.localReady) return;
    this.cb.send({ bye: true });
    this.localReady = false;
    this.teardownPeer();
    this.stopLocal();
    this.emit('off');
  }

  /** Mute / unmute the outgoing mic without leaving the call. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.local) for (const tr of this.local.getAudioTracks()) tr.enabled = !muted;
    this.cb.onStatus(this.status(), this.muted);
  }

  /** Apply a signaling beat from the peer (offer / answer / ICE / ready / bye). */
  async onSignal(signal: VoiceSignal): Promise<void> {
    if (signal.bye) {
      this.peerReady = false;
      this.teardownPeer();
      this.emit(this.localReady ? 'waiting' : 'off');
      return;
    }
    if (signal.ready) {
      this.peerReady = true;
      if (this.localReady) this.connect();
      return;
    }
    if (!this.localReady) return; // not in voice — ignore stray handshake frames
    if (!this.pc) this.connect(); // peer started negotiating before our `ready` arrived
    const pc = this.pc;
    if (!pc) return;

    try {
      if (signal.description) {
        const polite = this.cb.seat() === 1;
        const offerCollision = signal.description.type === 'offer' && (this.makingOffer || pc.signalingState !== 'stable');
        this.ignoreOffer = !polite && offerCollision;
        if (this.ignoreOffer) return; // impolite peer keeps its own offer; peer will retry
        await pc.setRemoteDescription(signal.description);
        if (signal.description.type === 'offer') {
          await pc.setLocalDescription();
          this.cb.send({ description: pc.localDescription ?? undefined });
        }
      } else if (signal.candidate !== undefined) {
        try {
          await pc.addIceCandidate(signal.candidate ?? undefined);
        } catch (err) {
          if (!this.ignoreOffer) throw err; // a candidate for a rolled-back offer is expected to fail
        }
      }
    } catch {
      /* a transient negotiation error; ICE will retry or the connection state will report failure */
    }
  }

  /** The peer left the room entirely — hold our mic but drop the dead connection. */
  peerGone(): void {
    this.peerReady = false;
    this.teardownPeer();
    if (this.localReady) this.emit('waiting');
  }

  /** The peer (re)joined the room — re-announce our mic so voice can re-establish. */
  peerBack(): void {
    if (this.localReady) this.cb.send({ ready: true });
  }

  /** Tear everything down (leaving online mode / room closed). */
  dispose(): void {
    this.localReady = false;
    this.peerReady = false;
    this.teardownPeer();
    this.stopLocal();
    this.emit('off');
  }

  // ---- internals ----

  /** Build the peer connection and let negotiation begin (both sides do this). */
  private connect(): void {
    if (this.pc || !this.local) return;
    this.emit('connecting');
    const pc = new RTCPeerConnection({ iceServers: iceServers() });
    this.pc = pc;
    for (const track of this.local.getTracks()) pc.addTrack(track, this.local);

    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await pc.setLocalDescription();
        this.cb.send({ description: pc.localDescription ?? undefined });
      } catch {
        /* setLocalDescription can race a rollback; perfect negotiation recovers */
      } finally {
        this.makingOffer = false;
      }
    };
    pc.onicecandidate = ({ candidate }) => this.cb.send({ candidate: candidate ? candidate.toJSON() : null });
    pc.ontrack = ({ streams }) => {
      this.audioEl.srcObject = streams[0] ?? null;
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'connected') this.emit('live');
      else if (s === 'failed') this.emit('failed');
      else if (s === 'disconnected' && this.localReady) this.emit('connecting');
    };
  }

  private teardownPeer(): void {
    if (this.pc) {
      this.pc.onnegotiationneeded = null;
      this.pc.onicecandidate = null;
      this.pc.ontrack = null;
      this.pc.onconnectionstatechange = null;
      this.pc.close();
      this.pc = null;
    }
    this.audioEl.srcObject = null;
    this.makingOffer = false;
    this.ignoreOffer = false;
  }

  private stopLocal(): void {
    if (this.local) for (const tr of this.local.getTracks()) tr.stop();
    this.local = null;
    this.muted = false;
  }

  private status(): VoiceStatus {
    if (!this.localReady) return 'off';
    if (!this.pc) return 'waiting';
    return this.pc.connectionState === 'connected' ? 'live' : 'connecting';
  }

  private emit(status: VoiceStatus): void {
    this.cb.onStatus(status, this.muted);
  }
}
